/**
 * i18n — helpers puros, SEM catálogo hardcoded.
 *
 * O catálogo de idiomas vive na tabela `locales` do banco (migração 0023);
 * adicionar um idioma é um INSERT, não um deploy. Os consumidores (API, menu,
 * admin) recebem as linhas do catálogo e usam estas funções para casar.
 *
 * Regra de detecção do cliente: idioma do APARELHO (navigator.languages),
 * nunca geolocalização.
 *
 * Códigos SEM região (#419): as 14 línguas oficiais usam só a base
 * ('pt', 'en', …). A entrada ainda ACEITA códigos regionais/legados
 * ('pt-BR', 'pt-PT', 'en-US', 'pt_BR'…) — `normalizeLocale()` converte para a
 * base — mas nada é GRAVADO nem EMITIDO com região.
 */

/** As 14 línguas oficiais, todas com código de base (sem região). */
export const OFFICIAL_LANGUAGES = [
  'pt',
  'en',
  'es',
  'fr',
  'de',
  'it',
  'nl',
  'zh',
  'ja',
  'ko',
  'ru',
  'ar',
  'hi',
  'el',
] as const
export type OfficialLanguage = (typeof OFFICIAL_LANGUAGES)[number]

/** Conjunto dos 14 códigos oficiais (base, sem região) — #465 QA 5640804090:
 *  só um destes códigos pode ser gravado como idioma confirmado do import. */
export const OFFICIAL_SET: ReadonlySet<string> = new Set<string>(OFFICIAL_LANGUAGES)

/** Idioma por omissão da plataforma. */
export const DEFAULT_LOCALE: OfficialLanguage = 'pt'

/**
 * Autónimos das 14 línguas — FONTE ÚNICA para seletores de idioma (admin,
 * landing, menu). A ordem de exibição é a de OFFICIAL_LANGUAGES.
 */
export const LOCALE_NATIVE_NAMES: Readonly<Record<OfficialLanguage, string>> = {
  pt: 'Português',
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
  nl: 'Nederlands',
  zh: '中文',
  ja: '日本語',
  ko: '한국어',
  ru: 'Русский',
  ar: 'العربية',
  hi: 'हिन्दी',
  el: 'Ελληνικά',
}

/** Idioma de fallback canónico para conteúdo (mesma língua, sem região). */
export const FALLBACK_LOCALE: OfficialLanguage = 'en'

/**
 * Aliases legados com região → base. Tudo o que não está aqui mas cuja base
 * é uma língua oficial também cai na base (ex.: 'en-AU' → 'en').
 */
const LOCALE_ALIASES: Readonly<Record<string, OfficialLanguage>> = {
  'pt-br': 'pt',
  'pt-pt': 'pt',
  ptbr: 'pt',
  ptpt: 'pt',
  'en-us': 'en',
  'en-gb': 'en',
  enus: 'en',
  engb: 'en',
  'es-es': 'es',
  'es-ar': 'es',
  'es-mx': 'es',
  eses: 'es',
  'fr-fr': 'fr',
  frfr: 'fr',
  'de-de': 'de',
  dede: 'de',
  'it-it': 'it',
  itit: 'it',
  'nl-nl': 'nl',
  nlnl: 'nl',
  'zh-cn': 'zh',
  'zh-hans': 'zh',
  zhcn: 'zh',
  'ja-jp': 'ja',
  jajp: 'ja',
  'ko-kr': 'ko',
  kokr: 'ko',
  'ru-ru': 'ru',
  ruru: 'ru',
  'hi-in': 'hi',
  hiin: 'hi',
  'el-gr': 'el',
  elgr: 'el',
}

/**
 * Normaliza qualquer código de idioma (novo ou legado) para o código canónico
 * sem região: trim, '_' → '-', minúsculas, alias regional → base. Um código
 * desconhecido devolve a base se esta for uma das 14 línguas oficiais
 * (ex.: 'en-AU' → 'en'), senão devolve a entrada em minúsculas (ex.: 'ca').
 */
export function normalizeLocale(input: string): string {
  const cleaned = input.trim().replace(/_/g, '-').toLowerCase()
  const alias = LOCALE_ALIASES[cleaned]
  if (alias) return alias
  const base = cleaned.split('-')[0] ?? cleaned
  if (OFFICIAL_SET.has(base)) return base
  return cleaned
}

/** Normaliza uma lista de locales (ex.: target_locales) e remove duplicados. */
export function normalizeLocaleList(locales: readonly string[]): string[] {
  return [...new Set(locales.map(normalizeLocale))]
}

/**
 * Locale de FORMATAÇÃO Intl (#419/#428): compõe a língua base com o país do
 * restaurante — `pt` + `PT` → 'pt-PT' («12,50 €»), `pt` + `BR` → 'pt-BR'
 * («R$ 12,50»), `en` + `GB` → 'en-GB'. SÓ para `Intl.NumberFormat`/
 * `DateTimeFormat`: nunca gravar, mostrar nem enviar este valor — a moeda e o
 * formato vêm do país, o idioma da interface continua sem região. Sem país,
 * usa a base.
 */
export function intlLocaleFor(lang: string, countryCode?: string | null): string {
  const base = normalizeLocale(lang)
  const cc = countryCode?.trim().toUpperCase()
  return base && cc ? `${base}-${cc}` : base
}

/** Linha do catálogo `locales` (formato servido pelas APIs). */
export type LocaleMeta = {
  /** Código canónico, sem região (ex.: 'pt') */
  code: string
  /** Label no admin (idioma — moeda) */
  label: string
  /** Nome nativo (para o seletor do cliente) */
  native: string
  /** Base do idioma BCP-47 — igual a `code` desde #419 */
  base: string
  currency: string
  rtl: boolean
  sortOrder?: number
  isActive?: boolean
}

/** Estrutura válida de locale BCP-47 curto — validação SEMÂNTICA (existe no
 *  catálogo?) é feita em runtime pela API contra a tabela `locales`. */
export const LOCALE_PATTERN = /^[a-zA-Z]{2,3}(-[a-zA-Z0-9]{1,8})*$/

/** Índice do catálogo para buscas O(1) sem estado global. */
export type LocaleIndex = {
  byCode: Map<string, LocaleMeta>
  byBase: Map<string, LocaleMeta[]>
}

export function buildLocaleIndex(metas: readonly LocaleMeta[]): LocaleIndex {
  const byCode = new Map<string, LocaleMeta>()
  const byBase = new Map<string, LocaleMeta[]>()
  for (const m of metas) {
    byCode.set(m.code, m)
    const list = byBase.get(m.base) ?? []
    list.push(m)
    byBase.set(m.base, list)
  }
  return { byCode, byBase }
}

export function localeMeta(index: LocaleIndex, code: string): LocaleMeta | undefined {
  return index.byCode.get(code)
}

/** RTL (ex.: árabe) — define dir do documento e ajustes de CSS. */
export function isRtl(index: LocaleIndex, code: string): boolean {
  return index.byCode.get(code)?.rtl ?? false
}

/**
 * Escolhe o melhor locale disponível a partir dos idiomas do APARELHO
 * (navigator.languages). Matcher BCP-47: código exato → mesma base de idioma
 * → default. Nunca usa geolocalização.
 *
 * `available` = metas dos locales que o restaurante oferece (catálogo filtrado).
 */
export function pickLocale(
  deviceLanguages: readonly string[],
  available: readonly LocaleMeta[],
  fallback: string,
): string {
  if (available.length === 0) return fallback
  const codes = available.map((a) => a.code)

  for (const raw of deviceLanguages) {
    // Normaliza códigos regionais do aparelho ('pt-BR' → 'pt') antes de casar.
    const lang = normalizeLocale(raw)
    // 1. match exato
    const exact = codes.find((c) => c.toLowerCase() === lang)
    if (exact) return exact
    // 2. mesma base de idioma
    const base = lang.split('-')[0]!
    const sameBase = available.find((a) => a.base.toLowerCase() === base)
    if (sameBase) return sameBase.code
  }
  return codes.includes(fallback) ? fallback : codes[0]!
}
